// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";

import svgIcon from '../../../../share/components/vue/SVGIcon';
import customProgress from "../../../../share/components/vue/CustomProgress";
import alert from "../../../../share/alert";
import apiService from '../../../../share/util/api/service';
import { executePolling } from '../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { pushObdAllDtc, pushObdSystemList } from "../../../../router/obd";
import diagnoseResource from "../../../../share/util/diagnose";

const SELECTABLE = 'selectable';
const UNSELECTABLE = 'unselectable';

export default {
  name: "obdSystemTop",
  data() {
    return {
      showVehicleModal: false,
      phaseMessage: '',
      progress: 0,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === "home") {
      const messages = this.$t("message.message_vehicle_dtc_confirm_back_home");
      alert.showConfirmation(this, null, messages, () => {
        // OKボタンが押下された場合はstart画面に戻す
        next();
      });
    } else {
      next();
    }
  },

  components: {
    'custom-Progress': customProgress,
    'svg-icon': svgIcon
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      obdDataId: state => state.obd.obdDataId,
      obdSystems: state => state.obd.systems,
    }),
    obdIndividualIconStyle() {
      return this.obdSystems?.length > 0 ? SELECTABLE : UNSELECTABLE;
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

  },

  methods: {
    ...mapActions('obd', ['updateObdDataId', 'updateObdSystems']),
    ...mapActions('auth', ['logout', 'resetSession']),

    /**
     * ALL DTC(OBD) へ遷移する
     */
    openAllDtc: function () {
      pushObdAllDtc(this);
    },

    /**
     * OBDシステム一覧画面へ遷移する
     */
    openObdIndividual: function () {
      pushObdSystemList(this);
    },

    /**
     * 車両情報取得(OBD)
     * @returns 車両情報取得成否
     */
    async startConnectionToOBDVehicle() {
      this.showVehicleModal = true;
      this.updateObdSystems([]);

      let result = false;
      result = await this.judeObdCommunicationStandard();

      if (result) {
        result = await this.readSystemName();
      }

      this.showVehicleModal = false;
      return result;
    },


    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        () => { this.logout(this); },
        // セッションリセット処理のコールバック
        () => { this.resetSession(this); },
        // その他エラー発生のコールバック
        () => { this.showUnexpectedError(); },
        // 注意 `...mapActions('auth', ['logout', 'resetSession']),` の追加が必要です。
      );
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
      alert.showError(this, null,
        messages,
        () => {
          // 何もしない
        });
    },

    // OBD通信規格判定(POST)
    async judeObdCommunicationStandard() {
      try {
        // 進捗状態を初期化
        this.progress = 0;
        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () => {
            const ret = await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/getVehicleObdInfo`,
              {
                project_id: this.vehicleInfo ? this.vehicleInfo.vehicle.project.id : null
              },
              this.operationScreen
            );
            this.progress = 25;
            return ret;
          },
          // 呼び出したい GET API を実装
          async (postApiResponse) => {
            return await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen);
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックの処理を実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          this.updateObdDataId(response.data.results.obd_data_id);
          return true;
        }
      } catch (error) {
        // noop
      } finally {
        this.progress = 50;
      }
      return false;
    },

    // システム名呼出し(POST)
    async readSystemName() {
      try {
        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () => {
            const ret = await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/readSystemName`,
              {},
              this.operationScreen
            );
            this.progress = 75;
            return ret;
          },
          // 呼び出したい GET API を実装
          async (postApiResponse) => await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen),
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックの処理を実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          this.updateObdSystems(response.data?.results?.obd_info);
          return true;
        }
      } catch (error) {
        // noop
      } finally {
        this.progress = 100;
      }
      return false;
    },
  },

  async mounted() {
    let result = false;
    // 車両情報取得(OBD)
    if (!this.obdSystems || !this.obdSystems.length) {
      // store に情報がない場合のみ、車両情報取得を実施する
      result = await this.startConnectionToOBDVehicle();
      if (!result) {
        return;
      }
    }

    // ツリーからノードが選択されて遷移してきた時は、クエリパラメータにより、OBD全DTC、OBD個別診断ページにリダイレクトする
    if (this.$route?.query?.obdMenu) {
      // OBD全DTC、OBD個別診断ページにリダイレクト
      if (this.$route.query.obdMenu === "allDtc") {
        // OBD全DTC画面にリダイレクト
        pushObdAllDtc(this);
      } else {
        // store に情報がない場合、リダイレクトしない
        if (this.obdSystems?.length > 0) {
          // OBD個別診断画面にリダイレクト
          pushObdSystemList(this, this.$route.query.systemNo, this.$route.query.funcKey);
        }
      }
      return;
    }
  }
};
